import { registerFunctionalComponent } from 'js/lib/components/component'
import { requestNative } from 'js/lib/api/api-request'
import { notifyBugsnag } from 'js/lib/bugsnag'

const SORTABLE_TABLE_CLASS = '.js-c-ui-table-sortable'
const ROW_CLASS = '.js-c-ui-table-sortable__row'
const HANDLE_CLASS = '.js-c-ui-table-sortable__handle'
const DRAGGED_ROW_OPACITY = '0.5'
const DEFAULT_OPACITY = '1'
const HIGHLIGHT_CLASSES = 'tw-outline-dashed tw-outline-blue-500 tw-outline-2 tw-outline-offset-2'
const SELECTED_ROW_CLASS = 'tw-bg-neutral-200 tw-outline-blue-500 tw-outline-1'
const FOCUS_OUTLINE_CLASS = 'tw-outline tw-outline-2 tw-outline-blue-500'
const DRAGGED_BORDER_CLASS = 'tw-border-neutral-200 tw-border-opacity-50'
// Insert an element after a reference element
const insertAfter = (newNode, referenceNode) => {
  referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling)
}

// Get the closest row element
const getRow = event => event.target.closest(ROW_CLASS)

// Toggle border style
const toggleBorder = (element, add) => {
  if (element) {
    if (add) {
      element.classList.add(...HIGHLIGHT_CLASSES.split(' '))
    } else {
      element.classList.remove(...HIGHLIGHT_CLASSES.split(' '))
    }
  }
}

// Handle row dragging
const onDragStart = event => {
  // Check if drag started from handle
  const handle = event.target.closest(HANDLE_CLASS)
  if (!handle) {
    event.preventDefault()
    return
  }
  
  const row = handle.closest(ROW_CLASS)
  if (row) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', null) // For Firefox
    
    // Use the entire row as the drag image instead of just the handle
    event.dataTransfer.setDragImage(row, 0, 0)
    
    row.style.opacity = DRAGGED_ROW_OPACITY
    row.classList.add(...SELECTED_ROW_CLASS.split(' '))
    row.dataset.dragged = 'true'
    
    // Apply grabbing cursor to the button inside the handle
    const button = handle.querySelector('button')
    if (button) {
      button.classList.remove('!tw-cursor-grab')
      button.classList.add('!tw-cursor-grabbing')
    }
  }
}

const onDragEnd = event => {
  const row = getRow(event)
  if (row) {
    row.style.opacity = DEFAULT_OPACITY
    delete row.dataset.dragged
    row.classList.remove(...SELECTED_ROW_CLASS.split(' '))
    toggleBorder(row, false)
    
    // Remove lighter border from all cells
    const cells = row.querySelectorAll('td')
    cells.forEach(cell => {
      cell.classList.remove(...DRAGGED_BORDER_CLASS.split(' '))
    })
    
    // Restore grab cursor and maintain focus on the handle
    const handle = row.querySelector(HANDLE_CLASS)
    if (handle) {
      const button = handle.querySelector('button')
      if (button) {
        button.classList.remove('!tw-cursor-grabbing')
        button.classList.add('!tw-cursor-grab')
      }
      // Maintain focus on handle after drop
      handle.focus()
    }
  }
}

// Handle visual feedback during drag
const onDragOver = event => {
  const row = getRow(event)
  // Only allow drop if hovering over a valid row
  if (row) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    toggleBorder(row, true)
  } else {
    event.dataTransfer.dropEffect = 'none'
  }
}

const onDragLeave = event => {
  const target = getRow(event)
  if (target) {
    toggleBorder(target, false)
  }
}

// Handle drop event and update row order
const onDrop = (event, tbody) => {
  event.preventDefault()
  const target = getRow(event)
  const draggedRow = document.querySelector(`[data-dragged="true"]`)

  // Only process drop if both target and dragged row exist, are different, and in same tbody
  if (target && draggedRow && draggedRow !== target && target.parentNode === tbody && draggedRow.parentNode === tbody) {
    const shouldInsertAfter =
      Array.prototype.indexOf.call(tbody.children, draggedRow) <
      Array.prototype.indexOf.call(tbody.children, target)
    
    if (shouldInsertAfter) {
      insertAfter(draggedRow, target)
    } else {
      tbody.insertBefore(draggedRow, target)
    }
    
    const table = tbody.closest(SORTABLE_TABLE_CLASS)
    updateOrder(table)
  }
  
  toggleBorder(target, false)
  if (draggedRow) {
    toggleBorder(draggedRow, false)
  }
}

// Update the row order by setting the `data-position` attribute
const updateOrder = (table) => {
  const rows = table.querySelectorAll(ROW_CLASS)
  rows.forEach((row, index) => {
    row.dataset.position = index + 1
    row.setAttribute('aria-label', `Row ${index + 1}`)
  })

  sendOrderUpdate(table)
}

// Send the updated order to the server
async function sendOrderUpdate(table) {
  const postToUrl = table.dataset.postToUrl
  
  if (!postToUrl) {
    return // No URL provided, skip AJAX update
  }

  const rows = table.querySelectorAll(ROW_CLASS)
  const rowOrder = Array.from(rows).map(row => ({
    id: row.getAttribute('data-id'),
    position: row.getAttribute('data-position'),
  }))

  try {
    await requestNative({
      url: postToUrl,
      method: 'POST',
      data: { row_order: rowOrder },
    })
    // If we reach here, the request was successful (response.ok was true)
  } catch (error) {
    // Check if this is a JSON parsing error (e.g., empty response body after successful save)
    const isJsonParseError = error instanceof SyntaxError && error.message?.includes('JSON')
    
    // JSON parse errors indicate the save succeeded but response was empty/invalid
    // Don't show alert or notify Bugsnag for these
    if (isJsonParseError) {
      return
    }
    
    // Only show alert for actual HTTP errors
    const isHttpError = error?.status && error.status >= 400
    
    if (isHttpError) {
      notifyBugsnag(error)
      window.alert('Failed to save the new order. Please try again or refresh the page.')
    }
    // For other errors, log but don't alert
    else if (error) {
      console.warn('Error after order update request:', error)
      notifyBugsnag(error)
    }
  }
}

// Handle keyboard navigation
const onKeyDown = event => {
  // Only handle arrow keys if focus is on a handle
  const handle = event.target.closest(HANDLE_CLASS)
  if (!handle) return
  
  const currentRow = handle.closest(ROW_CLASS)
  if (currentRow) {
    const table = handle.closest(SORTABLE_TABLE_CLASS)
    const tbody = table.querySelector('tbody')
    const allRows = Array.from(tbody.querySelectorAll(ROW_CLASS))

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        moveRow(currentRow, allRows, -1, table)
        break
      case 'ArrowDown':
        event.preventDefault()
        moveRow(currentRow, allRows, 1, table)
        break
    }
  }
}

// Move rows with keyboard
const moveRow = (row, allRows, direction, table) => {
  const currentIndex = allRows.indexOf(row)
  const targetIndex = currentIndex + direction

  if (targetIndex >= 0 && targetIndex < allRows.length) {
    const targetRow = allRows[targetIndex]
    if (direction < 0) {
      row.parentNode.insertBefore(row, targetRow)
    } else {
      insertAfter(row, targetRow)
    }
    // Keep focus on the handle after moving
    const handle = row.querySelector(HANDLE_CLASS)
    if (handle) {
      handle.focus()
    }
    updateOrder(table)
  }
}

// Handle focus on handle - add outline to row
const onHandleFocus = event => {
  const handle = event.target.closest(HANDLE_CLASS)
  if (handle) {
    const row = handle.closest(ROW_CLASS)
    if (row) {
      row.classList.add(...FOCUS_OUTLINE_CLASS.split(' '))
    }
  }
}

// Handle blur on handle - remove outline from row
const onHandleBlur = event => {
  const handle = event.target.closest(HANDLE_CLASS)
  if (handle) {
    const row = handle.closest(ROW_CLASS)
    if (row) {
      row.classList.remove(...FOCUS_OUTLINE_CLASS.split(' '))
    }
  }
}

function initSortableTable(component) {
  const table = component.element
  const tbody = table.querySelector('tbody')
  
  if (!tbody) return

  // Check for required handle components
  const handles = tbody.querySelectorAll(HANDLE_CLASS)
  if (handles.length === 0) {
    const errorMessage = 'Sortable table requires at least one handle component. Add "ui/table/sortable/handle" to your table rows.'
    console.error(errorMessage)
    notifyBugsnag(new Error(errorMessage))
    return
  }

  // Make all rows have classes and data attributes
  const rows = tbody.querySelectorAll('tr')
  rows.forEach((row, index) => {
    row.classList.add('js-c-ui-table-sortable__row')
    row.setAttribute('aria-label', `Row ${index + 1}`)
    row.dataset.position = index + 1
    
    // Add data-id if not present
    if (!row.dataset.id) {
      row.dataset.id = `row-${index + 1}`
    }
  })

  // Make handles draggable and focusable, and style buttons
  handles.forEach(handle => {
    handle.setAttribute('draggable', 'true')
    handle.setAttribute('tabindex', '0')
    
    // Add cursor to the button inside the handle
    const button = handle.querySelector('button')
    if (button) {
      button.classList.add('tw-cursor-grab')
    }
  })

  // Register event listeners
  component.useHandler(() => {
    tbody.addEventListener('dragstart', onDragStart)
    tbody.addEventListener('dragend', onDragEnd)
    tbody.addEventListener('dragover', onDragOver)
    tbody.addEventListener('dragleave', onDragLeave)
    tbody.addEventListener('drop', event => onDrop(event, tbody))
    tbody.addEventListener('keydown', onKeyDown)
    tbody.addEventListener('focusin', onHandleFocus)
    tbody.addEventListener('focusout', onHandleBlur)

    return () => {
      tbody.removeEventListener('dragstart', onDragStart)
      tbody.removeEventListener('dragend', onDragEnd)
      tbody.removeEventListener('dragover', onDragOver)
      tbody.removeEventListener('dragleave', onDragLeave)
      tbody.removeEventListener('drop', event => onDrop(event, tbody))
      tbody.removeEventListener('keydown', onKeyDown)
      tbody.removeEventListener('focusin', onHandleFocus)
      tbody.removeEventListener('focusout', onHandleBlur)
    }
  })
}

registerFunctionalComponent(SORTABLE_TABLE_CLASS, 'SortableTable', initSortableTable)
